import { useState, useEffect, useCallback } from 'react';
import { useUpdateSession } from './useSessions';

interface UseEquipmentStateOptions {
  session: any;
  isEdit: boolean;
  onSaveNeeded?: () => Promise<void>;
  markUnsavedChange?: (key: string) => void;
  clearUnsavedChange?: (key: string) => void;
}

interface UseEquipmentStateReturn {
  equipmentParameters: string[];
  setEquipmentParameters: (params: string[]) => void;
  equipmentSelected: any;
  setEquipmentSelected: (selected: any) => void;
  hasEquipmentChanges: boolean;
  saveEquipmentChanges: () => Promise<void>;
}

export function useEquipmentState({
  session,
  markUnsavedChange,
  clearUnsavedChange,
}: UseEquipmentStateOptions): UseEquipmentStateReturn {
  // Initialize state from session data
  const [equipmentParameters, setEquipmentParameters] = useState<string[]>(() => session?.equipment_parameters || []);

  const [equipmentSelected, setEquipmentSelected] = useState<any>(() => session?.equipment_selected || {});

  const updateSession = useUpdateSession(session?.id);

  // Track changes to equipmentParameters
  useEffect(() => {
    if (!markUnsavedChange || !clearUnsavedChange) return;

    const hasEquipmentParametersChanged =
      JSON.stringify(equipmentParameters) !== JSON.stringify(session?.equipment_parameters);

    if (hasEquipmentParametersChanged) {
      markUnsavedChange('equipmentParameterColumns');
    } else {
      clearUnsavedChange('equipmentParameterColumns');
    }
  }, [equipmentParameters, session?.equipment_parameters, markUnsavedChange, clearUnsavedChange]);

  // Track changes to equipmentSelected
  useEffect(() => {
    if (!markUnsavedChange || !clearUnsavedChange) return;

    const hasEquipmentSelectedChanged =
      JSON.stringify(equipmentSelected) !== JSON.stringify(session?.equipment_selected);

    if (hasEquipmentSelectedChanged) {
      markUnsavedChange('equipmentSelectedColumns');
    } else {
      clearUnsavedChange('equipmentSelectedColumns');
    }
  }, [equipmentSelected, session?.equipment_selected, markUnsavedChange, clearUnsavedChange]);

  // Reset state when session changes (e.g., after save/cancel)
  useEffect(() => {
    setEquipmentParameters(session?.equipment_parameters || []);
    setEquipmentSelected(session?.equipment_selected || {});
  }, [session?.equipment_parameters, session?.equipment_selected]);

  // Check if there are equipment changes
  const hasEquipmentChanges = useCallback(() => {
    const parametersChanged = JSON.stringify(equipmentParameters) !== JSON.stringify(session?.equipment_parameters);
    const selectedChanged = JSON.stringify(equipmentSelected) !== JSON.stringify(session?.equipment_selected);

    return parametersChanged || selectedChanged;
  }, [equipmentParameters, equipmentSelected, session]);

  // Save equipment changes to database
  const saveEquipmentChanges = useCallback(async () => {
    if (!hasEquipmentChanges() || !session?.id) return;

    const updates: any = {};

    if (JSON.stringify(equipmentParameters) !== JSON.stringify(session?.equipment_parameters)) {
      updates.equipment_parameters = equipmentParameters;
    }

    if (JSON.stringify(equipmentSelected) !== JSON.stringify(session?.equipment_selected)) {
      updates.equipment_selected = equipmentSelected;
    }

    if (Object.keys(updates).length > 0) {
      await updateSession.mutateAsync({
        id: session.id,
        ...updates,
      });

      // Clear unsaved change markers if available
      if (clearUnsavedChange) {
        clearUnsavedChange('equipmentParameterColumns');
        clearUnsavedChange('equipmentSelectedColumns');
      }
    }
  }, [equipmentParameters, equipmentSelected, session, updateSession, clearUnsavedChange, hasEquipmentChanges]);

  return {
    equipmentParameters,
    setEquipmentParameters,
    equipmentSelected,
    setEquipmentSelected,
    hasEquipmentChanges: hasEquipmentChanges(),
    saveEquipmentChanges,
  };
}
